import React, {useState} from "react";
import {useForm} from "react-hook-form";
import {Trans, useTranslation} from "react-i18next";
import {CustomerFactory} from "@connox/typescript-library";
import {getShopId} from "../lib/getShopId";
import {emailRegExp} from "../../helpers/emailRegExp";
import styles from "./NewsletterRegistrationInTextBlock.module.scss";
import {useCustomerSelector} from "../../store";
import {RegistrationVoucher} from "../../helpers/registrationVoucher";

const NewsletterRegistrationInTextBlock: React.FC = () => {
  const {customer} = useCustomerSelector();
  const {t} = useTranslation();
  const [customerEmail, setCustomerEmail] = useState("");

  const EmailInputBlock: React.FC = () => {
    const {
      register,
      handleSubmit,
      formState: {errors},
    } = useForm<{email: string}>({
      defaultValues: {
        email: customerEmail ?? customer.email ?? "",
      },
    });
    if (customerEmail) {
      const providerData = emailProvider(customerEmail);

      return (
        <div className={styles.successMessage}>
          <h3>{t("newsletter.confirmRegistration")}</h3>
          <p>
            <Trans
              i18nKey="newsletter.callToConfirmNewsletter"
              ns="translations"
              values={{customerEmail}}
              components={{
                a: <strong />,
              }}
            />
          </p>
          {providerData && (
            <a href={providerData.url} className="button" target="_blank" rel="noopener noreferrer">
              {t("general.goTo--provider", {provider: providerData.title})}
            </a>
          )}
        </div>
      );
    }

    const onSubmit = handleSubmit((data: {email: string}) => {
      void CustomerFactory.createOrUpdateCustomer(getShopId(), {email: data.email, newsletterFlag: true}).then(() => {
        setCustomerEmail(data.email);
      });
    });

    return (
      <form onSubmit={(evt: React.FormEvent<HTMLFormElement>) => void onSubmit(evt)}>
        <span className={styles.invalid}>{errors.email && errors.email.message}</span>
        <input
          type="email"
          placeholder={t("form.yourEmailAddress")}
          {...register("email", {
            required: true,
            pattern: {
              value: emailRegExp,
              message: t("form.invalidEmailAddress"),
            },
          })}
        />
        <button className="button" formAction="submit">
          {t("newsletter.subscribeNow")}
        </button>
      </form>
    );
  };

  const HeadlineAndParagraph = () => {
    if (!RegistrationVoucher.enabled) {
      return (
        <React.Fragment>
          <h2>{t("newsletter.connoxNewsletter")}</h2>
          <p>{t("newsletter.registrationDiscountShortText")}</p>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <h2>
          {t("newsletter.welcomeVoucher--amountFormatted", {amountFormatted: RegistrationVoucher.amountFormatted})}
        </h2>
        <p>
          <Trans
            i18nKey="newsletter.callToNewsletterAndGetCoupon--amountFormatted"
            ns="translations"
            values={{
              amountFormatted: RegistrationVoucher.amountFormatted,
            }}
            components={{
              b: <b />,
              s: <sup />,
            }}
          />
        </p>
      </React.Fragment>
    );
  };

  return (
    <div className={styles.wrapper}>
      <HeadlineAndParagraph />

      <ul className={styles.advantages}>
        <li>
          <span className="icon icon-check" />
          {t("newsletter.discountPromotionAlert")}
        </li>
        <li>
          <span className="icon icon-check" />
          {t("newsletter.latestDesignHighlights")}
        </li>
        <li>
          <span className="icon icon-check" />
          {t("newsletter.unsubscribeAnyTime")}
        </li>
      </ul>
      <EmailInputBlock />
    </div>
  );
};

export default NewsletterRegistrationInTextBlock;

const emailProvider = (email: string): {title: string; url: string} | null => {
  const emailMatch = /@((gmx|web|gmail|googlemail|t-online|hotmail|freenet|aol)\.(de|com|org|net))/i.exec(email);
  if (!emailMatch) return emailMatch;

  switch (emailMatch[1]) {
    case "googlemail.com":
    case "gmail.com":
      return {title: "GMail", url: "https://mail.google.com"};
    case "gmx.de":
      return {title: "GMX", url: "https://www.gmx.net"};
    default:
      return {title: emailMatch[2].toUpperCase(), url: "https://www." + emailMatch[1]};
  }
};
